import { orderedWorkflowVersions } from "../onboarding/workflowVersions";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, FactCheck } from "@mui/icons-material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { usePagination } from "../../../hooks/usePagination";
import { useAuthenticationStore } from "../../../stores/auth";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import LocalAuthoritySearchAutocomplete from "../../school/components/LocalAuthoritySearchAutocomplete";
import {
  isContactWorkflow,
  supportsSelection,
} from "../onboarding/workflowParticipants";
import ContactEnrollmentResults from "../onboarding/ContactEnrollmentResults";
import { notifyContactEnrollment } from "../onboarding/marketingNotifications";

import OutreachContactHistory from "../onboarding/OutreachContactHistory";

const verificationOptions = [
  ["", "All verification statuses"],
  ["publicly_confirmed", "Publicly confirmed"],
  ["unverified", "Unverified"],
  ["provider_verified", "Provider verified"],
  ["provider_invalid", "Provider invalid"],
];

export default function OutreachContactsPage() {
  const clients = useApiClient();
  const api = clients.marketing;
  const user = useAuthenticationStore((state) => state.user);
  const isSuperuser = user?.info?.activeContext?.contextType === "superuser";
  const [historyContact, setHistoryContact] = useState(null);
  const [stopping, setStopping] = useState(null);
  const [stopReason, setStopReason] = useState("");
  const stopOutreach = async () => {
    setBusy(true);
    try {
      const result = await api.stopOutreachContact(stopping.id, stopReason);
      enqueueSnackbar(result.message, {
        variant: result.success ? "success" : "error",
      });
      if (result.success) {
        setStopping(null);
        await contacts.refetch();
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [school, setSchool] = useState(null);
  const [localAuthority, setLocalAuthority] = useState(null);
  const [subject, setSubject] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [workflowId, setWorkflowId] = useState("");
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [enrollError, setEnrollError] = useState("");
  const [enrollmentResult, setEnrollmentResult] = useState(null);

  const openEnrollment = async () => {
    setEnrollOpen(true);
    setLoadingWorkflows(true);
    setEnrollError("");
    setWorkflowId("");
    setWorkflows([]);
    try {
      const data = await api.getManagement();
      if (data.marketingAutomationSettings?.isEnabled === false) {
        setEnrollError(
          "Resume global marketing automation before enrolling contacts.",
        );
        return;
      }
      setWorkflows(
        (data.marketingWorkflows || [])
          .filter(
            (workflow) =>
              isContactWorkflow(workflow) &&
              supportsSelection(workflow, "contact") &&
              workflow.isEnabled,
          )
          .flatMap((workflow) =>
            orderedWorkflowVersions(workflow)
              .filter((version) => version.status === "LIVE")
              .map((version) => ({ ...workflow, selectedVersion: version })),
          ),
      );
    } catch (error) {
      setEnrollError(error.message);
    } finally {
      setLoadingWorkflows(false);
    }
  };
  const enrollSelected = async () => {
    if (busy || !workflowId || !Object.keys(selected).length) return;
    setBusy(true);
    setEnrollError("");
    try {
      const result = await api.enrollContacts(
        Object.keys(selected),
        workflows.find((workflow) => workflow.selectedVersion.id === workflowId)
          .id,
        workflowId,
      );
      setEnrollmentResult(result);
      notifyContactEnrollment(result);
      setSelected({});
      setEnrollOpen(false);
      await contacts.refetch();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    clients.core
      .getAllActiveSubjects()
      .then((result) => setSubjects(result.allActiveSubjects || []));
  }, [clients]);

  const filters = useMemo(
    () => ({
      search: search || null,
      verificationStatus: verificationStatus || null,
      schoolId: school?.id || null,
      localAuthorityId: localAuthority?.id || null,
      subjectId: subject?.id || null,
    }),
    [localAuthority, school, search, subject, verificationStatus],
  );
  const fetchContacts = useCallback(
    (variables) => api.getOutreachContacts(variables),
    [api],
  );
  const contacts = usePagination(fetchContacts, 25, filters);
  const selectionRequest = useRef(0);
  useEffect(() => {
    selectionRequest.current += 1;
    return () => {
      selectionRequest.current += 1;
    };
  }, [filters]);
  const selectablePage = contacts.options.filter(
    (contact) => !contact.convertedAt,
  );
  const selectedOnPage = selectablePage.filter(
    (contact) => selected[contact.id],
  ).length;
  const togglePage = (_, checked) => {
    setSelected((current) => {
      const next = { ...current };
      selectablePage.forEach((contact) => {
        if (checked) next[contact.id] = contact;
        else delete next[contact.id];
      });
      return next;
    });
  };
  const selectAllMatching = async () => {
    if (busy) return;
    const request = ++selectionRequest.current;
    setBusy(true);
    try {
      const matching = {};
      let after = null;
      const cursors = new Set();
      do {
        const result = await fetchContacts({ ...filters, first: 100, after });
        if (request !== selectionRequest.current) return;
        (result.edges || []).forEach(({ node }) => {
          if (node && !node.convertedAt) matching[node.id] = node;
        });
        if (!result.pageInfo?.hasNextPage) break;
        const cursor = result.pageInfo.endCursor;
        if (!cursor || cursors.has(cursor))
          throw new Error("Unable to load all contacts. Please try again.");
        cursors.add(cursor);
        after = cursor;
      } while (after);
      setSelected((current) => ({ ...current, ...matching }));
    } catch (error) {
      if (request === selectionRequest.current)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const eligible = (contact) =>
    contact.discoveryType?.toLowerCase() === "inferred" &&
    contact.verificationStatus?.toLowerCase() === "unverified";
  const validationIds = Object.values(selected)
    .filter(eligible)
    .map((contact) => contact.id);
  const validateSelected = async () => {
    const ids = validationIds;
    if (!ids.length) return;
    setBusy(true);
    try {
      const result = await api.validateOutreachContacts(ids);
      if (!result.success) throw new Error(result.message);
      enqueueSnackbar(
        `${result.message}${result.skippedCount ? ` ${result.skippedCount} skipped.` : ""}`,
        { variant: result.queuedCount ? "success" : "warning" },
      );
      setSelected((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([id]) => !ids.includes(id)),
        ),
      );
      await contacts.refetch();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!isSuperuser) return <Navigate to="/" replace />;
  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Box>
          <Button
            component={RouterLink}
            to="/superuser/marketing/outreach"
            startIcon={<ArrowBack />}
          >
            Back to outreach
          </Button>
          <Typography variant="h4">Outreach contacts</Typography>
          <Typography color="text.secondary">
            Search, review, validate and enroll discovered outreach contacts.
          </Typography>
        </Box>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              gap={2}
              flexWrap="wrap"
            >
              <TextField
                size="small"
                label="Search name or school"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                sx={{ minWidth: 260 }}
              />
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <InputLabel>Verification status</InputLabel>
                <Select
                  label="Verification status"
                  value={verificationStatus}
                  onChange={(event) =>
                    setVerificationStatus(event.target.value)
                  }
                >
                  {verificationOptions.map(([value, label]) => (
                    <MenuItem key={value || "all"} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SchoolSearchAutocomplete
                school={school}
                onChange={(_event, value) => setSchool(value)}
                sx={{ minWidth: 260 }}
              />
              <LocalAuthoritySearchAutocomplete
                value={localAuthority}
                onChange={setLocalAuthority}
              />
              <Autocomplete
                size="small"
                options={subjects}
                value={subject}
                onChange={(_event, value) => setSubject(value)}
                getOptionLabel={(option) => option.name}
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField {...params} label="Subject" />
                )}
              />
            </Stack>
          </CardContent>
        </Card>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <FormControlLabel
              label="Select page"
              control={
                <Checkbox
                  disabled={busy || contacts.loading || !selectablePage.length}
                  checked={
                    selectablePage.length > 0 &&
                    selectedOnPage === selectablePage.length
                  }
                  indeterminate={
                    selectedOnPage > 0 && selectedOnPage < selectablePage.length
                  }
                  onChange={togglePage}
                />
              }
            />
            <Button
              onClick={selectAllMatching}
              disabled={busy || contacts.loading || !contacts.options.length}
            >
              Select all matching contacts
            </Button>
            <Button
              onClick={() => setSelected({})}
              disabled={busy || !Object.keys(selected).length}
            >
              Clear selection
            </Button>
            <Typography>{Object.keys(selected).length} selected</Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<FactCheck />}
            disabled={busy || !validationIds.length}
            onClick={validateSelected}
          >
            Validate selected
          </Button>
          <Button
            variant="contained"
            disabled={busy || !Object.keys(selected).length}
            onClick={openEnrollment}
          >
            Enroll selected
          </Button>
        </Stack>
        <ContactEnrollmentResults result={enrollmentResult} />
        {contacts.loading && <CircularProgress />}
        {contacts.error && (
          <Alert severity="error">{contacts.error.message}</Alert>
        )}
        {!contacts.loading && !contacts.options.length && (
          <Alert severity="info">No contacts match these filters.</Alert>
        )}
        <Stack spacing={1.5}>
          {contacts.options.map((contact) => (
            <Card key={contact.id} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                >
                  <Checkbox
                    disabled={busy || Boolean(contact.convertedAt)}
                    inputProps={{ "aria-label": `Select ${contact.email}` }}
                    checked={Boolean(selected[contact.id])}
                    onChange={() =>
                      setSelected((current) => {
                        const next = { ...current };
                        if (next[contact.id]) delete next[contact.id];
                        else next[contact.id] = contact;
                        return next;
                      })
                    }
                  />
                  <Box flex={1}>
                    <Typography variant="h6">
                      {contact.firstName} {contact.lastName}
                    </Typography>
                    <Typography>{contact.email}</Typography>
                    <Typography color="text.secondary">
                      {contact.jobTitle} · {contact.school.name}
                      {contact.school.localAuthority?.name
                        ? ` · ${contact.school.localAuthority.name}`
                        : ""}
                    </Typography>
                    <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                      {contact.subjects.map((item) => (
                        <Chip key={item.id} size="small" label={item.name} />
                      ))}
                    </Stack>
                  </Box>
                  <Stack alignItems={{ sm: "flex-end" }} spacing={0.5}>
                    {contact.convertedAt && <Chip label="Converted to user" />}
                    <Button onClick={() => setHistoryContact(contact)}>
                      View history
                    </Button>
                    {contact.hasActiveOutreach && (
                      <Button
                        color="warning"
                        disabled={busy}
                        onClick={() => {
                          setStopReason("");
                          setStopping(contact);
                        }}
                      >
                        Stop outreach
                      </Button>
                    )}
                    <Chip
                      size="small"
                      color={
                        contact.verificationStatus === "provider_invalid"
                          ? "error"
                          : contact.verificationStatus === "unverified"
                            ? "warning"
                            : "success"
                      }
                      label={contact.verificationStatus.replaceAll("_", " ")}
                    />
                    <Typography variant="caption">
                      {contact.discoveryType} · {contact.confidence} confidence
                    </Typography>
                    {contact.validationStatus && (
                      <Typography variant="caption">
                        Hunter: {contact.validationStatus}
                        {contact.validationScore != null
                          ? ` (${contact.validationScore})`
                          : ""}
                      </Typography>
                    )}
                    {contact.validationCheckedAt && (
                      <Typography variant="caption">
                        Checked{" "}
                        {new Date(contact.validationCheckedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
        {contacts.options.length > 0 && (
          <Pagination
            page={contacts.page}
            count={
              contacts.pageInfo.hasNextPage ? contacts.page + 1 : contacts.page
            }
            onChange={contacts.handlePageChange}
          />
        )}
        {historyContact && (
          <OutreachContactHistory
            api={api}
            contact={historyContact}
            onClose={() => setHistoryContact(null)}
          />
        )}
        <Dialog
          open={Boolean(stopping)}
          onClose={() => !busy && setStopping(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Stop outreach</DialogTitle>
          <DialogContent>
            <Stack spacing={2} pt={1}>
              <Typography>
                Stop all active outreach journeys for {stopping?.email}? Contact
                and delivery history will be retained.
              </Typography>
              <TextField
                label="Reason for stopping"
                value={stopReason}
                onChange={(event) => setStopReason(event.target.value)}
                disabled={busy}
                multiline
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStopping(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={stopOutreach}
              disabled={busy || !stopReason.trim()}
            >
              Confirm stop
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={enrollOpen}
          onClose={() => {
            if (!busy) setEnrollOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Enroll selected contacts</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <Typography>
                Enroll {Object.keys(selected).length} selected contacts.
                Eligibility is checked again for each contact; ineligible or
                already enrolled contacts will be reported individually.
              </Typography>
              {enrollError && <Alert severity="error">{enrollError}</Alert>}
              {loadingWorkflows ? (
                <CircularProgress />
              ) : workflows.length ? (
                <TextField
                  select
                  label="Outreach workflow"
                  value={workflowId}
                  onChange={(event) => setWorkflowId(event.target.value)}
                  disabled={busy}
                >
                  {workflows.map((workflow) => (
                    <MenuItem
                      key={workflow.selectedVersion.id}
                      value={workflow.selectedVersion.id}
                    >
                      {workflow.name} · v
                      {workflow.selectedVersion.versionNumber}
                      {workflow.selectedVersion.id ===
                      workflow.defaultVersion?.id
                        ? " · Default"
                        : ""}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                !enrollError && (
                  <Alert severity="info">
                    Publish and enable an outreach workflow before enrolling
                    contacts.
                  </Alert>
                )
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setEnrollOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={busy || loadingWorkflows || !workflowId}
              onClick={enrollSelected}
            >
              Confirm enrollment
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </FruitifyContainer>
  );
}
